/* screens/workspace/feed/ConfirmActionCard.jsx — подтверждение AI-правки сущности (S6).
   Inline-карточка «было → станет» (себест./FC%/цена) с явным подтверждением/отменой.
   Единый визуальный язык AI-артефакта с DraftEntityCard (маркер «AI предлагает»).
   State-машина и commit/cancel/restore — общий хук usePendingChange (его же использует
   канвас изменений ChangeCanvas; пока канвас открыт, MessageList эту карточку прячет).
   Боевой путь: commit → POST /api/chats/pending-changes/{id}/commit/ (200 {committed,
   restore_version_number,target}; 409 {error:'stale'|'expired'|'gone'} → stale-стейт); cancel →
   .../cancel/; откат → versioning-REST (restore_version_number). Decimal→Number и fc/selling-
   именование скрывает adaptConfirmAction (preview уже нормализован в провайдере).
   Источник: 07 §2.14, 05 сценарий 8. ХЕНДОФФ → frontend/components/chat/ConfirmActionCard.jsx. */

import React from 'react';
import { Icon } from '../../../components/ui/Icon.jsx';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';
import { KIND_HREF, KIND_LABEL, usePendingChange } from './usePendingChange.js';

  const { useNavigate } = RR;

  function DiffRow({ label, before, after, fmtFn, betterWhenLower }) {
    if (before == null && after == null) return null;
    const changed = before !== after;
    const dirGood = betterWhenLower == null ? null : (after < before ? betterWhenLower : !betterWhenLower);
    return (
      <div className="ai-diff__row">
        <span className="ai-diff__key">{label}</span>
        <span className="ai-diff__from num">{fmtFn(before)}</span>
        <Icon name="arrow-right" size={13} className="ai-diff__arr" />
        <span className={'ai-diff__to num' + (changed && dirGood === true ? ' is-good' : changed && dirGood === false ? ' is-bad' : '')}>{fmtFn(after)}</span>
      </div>
    );
  }

  function ConfirmActionCard({ pc }) {
    const navigate = useNavigate();
    const { status, busy, applied, restoreRef, commit, cancel, restore, restoreIdle } = usePendingChange(pc);
    const p = pc.preview || {};
    const fcBefore = p.fc_pct_before != null ? p.fc_pct_before : p.fc_before;
    const fcAfter = p.fc_pct_after != null ? p.fc_pct_after : p.fc_after;
    const sellBefore = p.selling_price_before != null ? p.selling_price_before : p.price_before;
    const sellAfter = p.selling_price_after != null ? p.selling_price_after : p.price_after;

    const open = () => navigate(KIND_HREF(pc));

    if (status === 'cancelled') {
      return <div className="ai-card ai-card--collapsed"><Icon name="x-circle" size={15} /><span>Правка «{pc.title}» отклонена</span><button type="button" className="ai-card__relink" onClick={restoreIdle}>Вернуть</button></div>;
    }

    return (
      <div className={'ai-card ai-card--confirm' + (applied ? ' is-applied' : '') + (status === 'stale' ? ' is-stale' : '')}>
        <div className="ai-card__head">
          <span className="ai-card__mark" aria-hidden="true"><Icon name="sparkles" size={13} /></span>
          <span className="ai-card__kicker">AI предлагает правку · {KIND_LABEL[pc.kind] || 'сущность'}</span>
          {applied && <span className="ai-card__state ai-card__state--ok"><Icon name="check" size={13} />{status === 'restored' ? 'Откатано' : 'Применено'}</span>}
          {!applied && status !== 'stale' && <span className="ai-card__state"><Icon name="clock" size={13} />ожидает</span>}
          {status === 'stale' && <span className="ai-card__state ai-card__state--mute">устарело</span>}
        </div>
        <div className="ai-card__title">{pc.title}</div>
        <div className="ai-diff">
          <DiffRow label="Себестоимость" before={p.cost_before} after={p.cost_after} fmtFn={(v) => fmt.money(v)} betterWhenLower={true} />
          <DiffRow label="Food cost" before={fcBefore} after={fcAfter} fmtFn={(v) => v == null ? '—' : fmt.percent(v)} betterWhenLower={true} />
          <DiffRow label="Цена продажи" before={sellBefore} after={sellAfter} fmtFn={(v) => fmt.money(v)} betterWhenLower={false} />
        </div>
        {status === 'error' && <div className="ai-card__err"><Icon name="alert-circle" size={14} /> Не удалось применить. Повторите.</div>}
        {status === 'stale' && <div className="ai-card__err ai-card__err--mute"><Icon name="alert-triangle" size={14} /> Сущность изменилась — правку нельзя применить.</div>}
        <div className="ai-card__foot">
          {(status === 'idle' || status === 'error') && (
            <>
              <button type="button" className="btn btn--primary btn--sm" onClick={commit}>{status === 'error' ? 'Повторить' : 'Подтвердить'}</button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={open}>Открыть</button>
              <button type="button" className="ai-card__link" onClick={cancel}>Отмена</button>
            </>
          )}
          {busy && <button type="button" className="btn btn--primary btn--sm" disabled><Icon name="spinner" size={14} className="spin" /><span className="btn__label">{status === 'restoring' ? 'Откат…' : 'Применяю…'}</span></button>}
          {status === 'committed' && (<><button type="button" className="btn btn--ghost btn--sm" onClick={open}>Открыть</button>{restoreRef && <button type="button" className="ai-card__link" onClick={restore}><Icon name="refresh" size={13} /> Откатить</button>}</>)}
          {status === 'restored' && <button type="button" className="btn btn--ghost btn--sm" onClick={open}>Открыть</button>}
          {status === 'stale' && <button type="button" className="btn btn--ghost btn--sm" onClick={open}>Открыть сущность</button>}
        </div>
      </div>
    );
  }

export { ConfirmActionCard };
